import { z } from 'zod';
import rawModelData from '../data/official-models.json';
import { jobKindSchema } from '../schemas/job';
const STATIC_VISIBLE_MODEL_IDS = new Set([
    'google/nano-banana-2',
    'google/nano-banana-pro',
    'black-forest-labs/flux-kontext-max',
    'google/veo-3',
    'google/veo-3-fast',
    'xai/grok-imagine-image',
    'xai/grok-imagine-video'
]);
const parseVersionFromSlug = (slug, prefix) => {
    const pattern = new RegExp(`${prefix}(\\d+(?:\\.\\d+)?)`, 'i');
    const match = pattern.exec(slug);
    if (!match) {
        return null;
    }
    const value = Number.parseFloat(match[1]);
    return Number.isNaN(value) ? null : value;
};
const pricingTierSchemaBase = z.object({
    price: z.number().nonnegative(),
    currency: z.string().default('USD'),
    unit: z.string(),
    metric: z.string(),
    priceText: z.string().optional(),
    description: z.string().optional()
});
const retailPricingSchema = pricingTierSchemaBase.extend({
    credits: z.number().int().nonnegative(),
    minimumCredits: z.number().int().positive().optional()
});
const pricingCriteriaSchema = z.object({
    key: z.string().optional(),
    title: z.string().optional(),
    description: z.string().optional(),
    type: z.string().optional(),
    subtype: z.string().optional(),
    value: z.union([z.string(), z.number(), z.boolean()]).optional()
});
const pricingVariantSchema = pricingTierSchemaBase.extend({
    criteria: z.array(pricingCriteriaSchema).optional(),
    retailPrice: z.number().nonnegative().optional(),
    retailCredits: z.number().int().nonnegative().optional()
});
const modelUiConfigSchema = z.object({
    hiddenSettings: z.array(z.string()).optional(),
    relaxedDefaults: z.record(z.string(), z.unknown()).optional(),
    labelOverrides: z.record(z.string(), z.string()).optional(),
    optionLabelOverrides: z.record(z.string(), z.record(z.string(), z.string())).optional()
});
const modelSettingsSchema = z
    .object({
    required: z.array(z.string()).optional(),
    properties: z.record(z.string(), z.unknown()).optional()
})
    .optional();
export const replicateModelSchema = z.object({
    id: z.string(),
    label: z.string(),
    description: z.string().optional(),
    kind: jobKindSchema,
    model: z.string(),
    owner: z.string(),
    slug: z.string(),
    url: z.string().url().optional(),
    coverImageUrl: z.string().url().optional(),
    defaultSettings: z.record(z.string(), z.unknown()).default({}),
    pricing: z.object({
        provider: pricingTierSchemaBase,
        retail: retailPricingSchema,
        variants: z.array(pricingVariantSchema).optional(),
        notes: z.string().optional()
    }),
    settings: modelSettingsSchema,
    ui: modelUiConfigSchema.optional()
});
const readString = (value) => {
    if (typeof value === 'string') {
        const trimmed = value.trim();
        return trimmed.length > 0 ? trimmed : undefined;
    }
    return undefined;
};
const readNumber = (value) => {
    if (typeof value === 'number' && Number.isFinite(value)) {
        return value;
    }
    if (typeof value === 'string') {
        const parsed = Number.parseFloat(value);
        return Number.isFinite(parsed) ? parsed : undefined;
    }
    return undefined;
};
const readBoolean = (value) => typeof value === 'boolean' ? value : undefined;
const readStringFrom = (...values) => {
    for (const value of values) {
        const result = readString(value);
        if (result) {
            return result;
        }
    }
    return undefined;
};
const readNumberFrom = (...values) => {
    for (const value of values) {
        const result = readNumber(value);
        if (result !== undefined) {
            return result;
        }
    }
    return undefined;
};
const isRecord = (value) => typeof value === 'object' && value !== null && !Array.isArray(value);
const sanitizeStringRecord = (input) => {
    if (!isRecord(input)) {
        return undefined;
    }
    const result = {};
    for (const [key, value] of Object.entries(input)) {
        if (typeof value === 'string' && value.length > 0) {
            result[key] = value;
        }
    }
    return Object.keys(result).length ? result : undefined;
};
const sanitizeNestedStringRecord = (input) => {
    if (!isRecord(input)) {
        return undefined;
    }
    const result = {};
    for (const [key, value] of Object.entries(input)) {
        const sanitized = sanitizeStringRecord(value);
        if (sanitized) {
            result[key] = sanitized;
        }
    }
    return Object.keys(result).length ? result : undefined;
};
const sanitizeModelUiConfig = (input) => {
    if (!isRecord(input)) {
        return undefined;
    }
    const hiddenSettings = Array.isArray(input.hiddenSettings)
        ? input.hiddenSettings.filter((value) => typeof value === 'string' && value.length > 0)
        : undefined;
    const relaxedDefaults = isRecord(input.relaxedDefaults) ? input.relaxedDefaults : undefined;
    const labelOverrides = sanitizeStringRecord(input.labelOverrides);
    const optionLabelOverrides = sanitizeNestedStringRecord(input.optionLabelOverrides);
    const hasContent = Boolean(hiddenSettings?.length) || Boolean(relaxedDefaults) || Boolean(labelOverrides) || Boolean(optionLabelOverrides);
    if (!hasContent) {
        return undefined;
    }
    return {
        hiddenSettings,
        relaxedDefaults,
        labelOverrides,
        optionLabelOverrides
    };
};
const sanitizeModelSettings = (input) => {
    if (!isRecord(input)) {
        return undefined;
    }
    const required = Array.isArray(input.required)
        ? input.required.filter((item) => typeof item === 'string' && item.length > 0)
        : undefined;
    const properties = isRecord(input.properties)
        ? input.properties
        : undefined;
    if (!required?.length && !properties) {
        return undefined;
    }
    return {
        required: required?.length ? required : undefined,
        properties
    };
};
const toCriterion = (input) => {
    if (!isRecord(input)) {
        return null;
    }
    const criterion = {
        key: readString(input.key),
        title: readString(input.title),
        description: readString(input.description),
        type: readString(input.type),
        subtype: readString(input.subtype)
    };
    const rawValue = input.value;
    if (typeof rawValue === 'string' || typeof rawValue === 'number') {
        criterion.value = rawValue;
    }
    else if (typeof rawValue === 'boolean') {
        const booleanValue = readBoolean(rawValue);
        if (booleanValue !== undefined) {
            criterion.value = booleanValue;
        }
    }
    return criterion;
};
const mapCriteria = (input) => {
    if (!Array.isArray(input)) {
        return undefined;
    }
    const normalized = input
        .map((item) => toCriterion(item))
        .filter((criterion) => criterion !== null);
    return normalized.length ? normalized : undefined;
};
const createModel = (input) => replicateModelSchema.parse(input);
const rawModels = rawModelData;
const isKlingModelVisible = (model) => {
    if (model.owner !== 'kwaivgi' || !model.slug.startsWith('kling-v')) {
        return false;
    }
    const version = parseVersionFromSlug(model.slug, 'kling-v');
    return version !== null && version >= 2.1;
};
const isWanModelVisible = (model) => {
    if (!model.slug.startsWith('wan-')) {
        return false;
    }
    const version = parseVersionFromSlug(model.slug, 'wan-');
    return version !== null && version >= 2.5;
};
const isModelVisible = (model) => STATIC_VISIBLE_MODEL_IDS.has(model.id) || isKlingModelVisible(model) || isWanModelVisible(model);
const toNotes = (model) => {
    const notes = [];
    const variantsCount = Array.isArray(model.pricing?.variants) ? model.pricing?.variants.length : 0;
    if (variantsCount) {
        notes.push(`Additional pricing variants available (${variantsCount}). Review replicate.com/${model.id} for full details.`);
    }
    const providerCriteriaSource = isRecord(model.pricing?.provider) ? (model.pricing?.provider)['criteria'] : undefined;
    const providerCriteria = mapCriteria(providerCriteriaSource);
    if (providerCriteria?.length) {
        const criteriaDescriptions = providerCriteria
            .map((criterion) => {
            const label = readStringFrom(criterion.title, criterion.key) ?? 'criteria';
            const value = criterion.value;
            return value !== undefined ? `${label}: ${value}` : label;
        })
            .filter(Boolean)
            .join(', ');
        if (criteriaDescriptions) {
            notes.push(`Pricing tier criteria: ${criteriaDescriptions}`);
        }
    }
    return notes.join(' ');
};
const MODELS = rawModels
    .map((model) => {
    const providerTier = isRecord(model.pricing?.provider) ? model.pricing?.provider : null;
    const retailTier = isRecord(model.pricing?.retail) ? model.pricing?.retail : null;
    if (!providerTier || !retailTier) {
        return null;
    }
    const notes = toNotes(model);
    const kindResult = jobKindSchema.safeParse(model.kind);
    if (!kindResult.success) {
        return null;
    }
    const rawVariants = Array.isArray(model.pricing?.variants)
        ? model.pricing?.variants
        : [];
    const normalizeVariant = (variantRecord) => {
        const criteria = mapCriteria(variantRecord['criteria']);
        const price = readNumberFrom(variantRecord['price']) ?? 0;
        const currency = readStringFrom(variantRecord['currency'], providerTier['currency'], retailTier['currency']) ?? 'USD';
        const unit = readStringFrom(variantRecord['unit'], providerTier['unit'], retailTier['unit']) ?? 'per run';
        const metric = readStringFrom(variantRecord['metric'], providerTier['metric'], retailTier['metric']) ?? 'run';
        const priceText = readStringFrom(variantRecord['priceText'], variantRecord['price_text']);
        const description = readString(variantRecord['description']);
        const retailPrice = readNumberFrom(variantRecord['retailPrice'], variantRecord['retail_price']);
        const retailCredits = readNumberFrom(variantRecord['retailCredits'], variantRecord['retail_credits']);
        return {
            price,
            currency,
            unit,
            metric,
            priceText: priceText ?? undefined,
            description: description ?? undefined,
            criteria,
            retailPrice,
            retailCredits
        };
    };
    const variants = rawVariants
        .map((variant) => (isRecord(variant) ? normalizeVariant(variant) : null))
        .filter((variant) => variant !== null);
    const minimumCredits = readNumberFrom(retailTier['minimumCredits'], retailTier['minimum_credits']);
    const retailPriceValue = readNumber(retailTier['price']) ?? 0;
    const computedCredits = readNumber(retailTier['credits']) ?? Math.round(retailPriceValue * 100);
    const sanitizedUi = sanitizeModelUiConfig(model.ui);
    const providerPrice = readNumber(providerTier['price']) ?? 0;
    const providerCurrency = readStringFrom(providerTier['currency'], retailTier['currency']) ?? 'USD';
    const providerUnit = readStringFrom(providerTier['unit'], retailTier['unit']) ?? 'per run';
    const providerMetric = readStringFrom(providerTier['metric'], retailTier['metric']) ?? 'run';
    const providerPriceText = readStringFrom(providerTier['priceText'], providerTier['price_text']);
    const providerDescription = readString(providerTier['description']);
    const retailCurrency = readStringFrom(retailTier['currency'], providerCurrency) ?? providerCurrency;
    const retailUnit = readStringFrom(retailTier['unit'], providerUnit) ?? providerUnit;
    const retailMetric = readStringFrom(retailTier['metric'], providerMetric) ?? providerMetric;
    const retailPriceText = readStringFrom(retailTier['priceText'], retailTier['price_text']);
    const retailDescription = readString(retailTier['description']);
    const defaultSettings = (isRecord(model.defaultSettings) ? model.defaultSettings : undefined) ||
        (isRecord(model.default_settings) ? model.default_settings : undefined) ||
        {};
    const coverImageUrl = readStringFrom(model.coverImageUrl, model.cover_image_url);
    const settings = sanitizeModelSettings(model.settings);
    return createModel({
        id: model.id,
        label: model.label,
        description: readString(model.description),
        kind: kindResult.data,
        model: model.model,
        owner: model.owner,
        slug: model.name,
        url: readString(model.url),
        coverImageUrl: coverImageUrl ?? undefined,
        defaultSettings,
        pricing: {
            provider: {
                price: providerPrice,
                currency: providerCurrency,
                unit: providerUnit,
                metric: providerMetric,
                priceText: providerPriceText ?? undefined,
                description: providerDescription ?? undefined
            },
            retail: {
                price: retailPriceValue,
                currency: retailCurrency,
                unit: retailUnit,
                metric: retailMetric,
                priceText: retailPriceText ?? undefined,
                description: retailDescription ?? undefined,
                credits: Math.max(1, computedCredits),
                minimumCredits
            },
            variants: variants.length ? variants : undefined,
            notes: notes || undefined
        },
        settings,
        ui: sanitizedUi
    });
})
    .filter((model) => model !== null);
export const listOfficialReplicateModels = () => MODELS.filter((model) => isModelVisible(model));
export const listOfficialReplicateModelsByKind = (kind) => MODELS.filter((model) => model.kind === kind && isModelVisible(model));
export const getReplicateModelById = (id) => MODELS.find((model) => model.id === id);
//# sourceMappingURL=replicate.js.map
